import { useEffect, useState } from 'react';
import { CMSPage, Layout } from '../Types/cms';
import { globalProperties } from '../Services/global';
import { useBizMutation, useBizQuery } from './useBizQuery';
import { getCMSPagesByIds, getHtmlSegments, saveHtmlSegment } from '../Services/cms';
import { useQueryClient } from '@tanstack/react-query';

export const useLayout = (layoutKey?: string): Layout | null => {
	const [layout, setLayout] = useState<Layout | null>(null);

	useEffect(() => {
		const loadLayout = async () => {
			try {
				await import(`../../CommonData/Layouts/${layoutKey}.scss`);
				const data = (await import(`../../CommonData/Layouts/${layoutKey}.json`)) as Layout;
				setLayout(data);
			} catch (error) {
				console.error('Error getting layout', error);
				return;
			}
		};

		if ((layoutKey?.length ?? 0) > 0) {
			loadLayout();
		}
	}, [layoutKey]);

	return layout;
};

export const useCMSPage = (pageKey?: string): CMSPage | null => {
	const [page, setPage] = useState<CMSPage | null>(null);
	const layout = useLayout(page?.layoutKey);

	useEffect(() => {
		const loadPage = async () => {
			try {
				const data = (await import(
					`../../Customers/${globalProperties.portalAlias}/Data/Pages/${pageKey}.json`
				)) as CMSPage;
				setPage({
					...data,
					layout: layout ?? undefined,
				});
			} catch (error) {
				console.error('Error getting page', error);
				return;
			}
		};

		if ((pageKey?.length ?? 0) > 0) {
			loadPage();
		}
	}, [pageKey]);

	useEffect(() => {
		if (page && layout) {
			setPage({ ...page, layout });
		}
	}, [layout]);

	return page;
};

const QUERYKEY_HTML_SEGMENT = 'HTML_SEGMENT';

const getCombinedKey = (prefixKey: string, contentKey: string) => `${prefixKey}_${contentKey}`;

export const useHtmlSegment = (prefixKey: string, contentKey: string, defaultContent?: string) => {
	const { data: htmlSegments, statusState } = useBizQuery([QUERYKEY_HTML_SEGMENT, prefixKey], async () =>
		getHtmlSegments(prefixKey)
	);

	const getContent = () => {
		if (!htmlSegments || statusState !== 'SUCCESS') return '';

		const htmlSegment = htmlSegments.find((x) => x.Key === getCombinedKey(prefixKey, contentKey));

		if (htmlSegment?.Value) {
			return htmlSegment.Value;
		}

		return defaultContent ?? '';
	};

	return { content: getContent(), statusState };
};

export const useSaveHtmlSegment = (prefixKey: string, contentKey: string) => {
	const saveHtmlSegmentWrapperFn = (params: { content: string }) => {
		return saveHtmlSegment(getCombinedKey(prefixKey, contentKey), params.content);
	};
	const queryClient = useQueryClient();
	const { mutate, statusState } = useBizMutation(saveHtmlSegmentWrapperFn);

	useEffect(() => {
		if (statusState === 'SUCCESS') {
			queryClient.invalidateQueries({ queryKey: [QUERYKEY_HTML_SEGMENT, prefixKey] });
		}
	}, [statusState]);

	return {
		saveHtmlSegment: (content: string) => mutate({ content }),
		statusState,
	};
};

const QUERYKEY_CMS_PAGES_BY_IDS = 'CMS_PAGES_BY_IDS';

export const useGetCMSPagesByIds = (ids: number[], enabled = true) => {
	const normalizedIds = [...new Set(ids)].sort((a, b) => a - b);
	const { data, statusState } = useBizQuery(
		[QUERYKEY_CMS_PAGES_BY_IDS, normalizedIds],
		() => getCMSPagesByIds(normalizedIds),
		enabled && normalizedIds.length > 0
	);

	return { pages: data ?? [], statusState };
};
